import { createAsyncThunk } from '@reduxjs/toolkit';
import { graphqlRequest, hasGraphQLNetworkError } from '~/adapters/graphqlRequest';
import { normalize } from 'normalizr';
import {
  AddSurveyTemplateDocument,
  AddSurveyTemplateMutationResult,
  CloneSurveyTemplateDocument,
  CloneSurveyTemplateMutationResult,
  DeleteSurveyTemplateDocument,
  DeleteSurveyTemplateMutationResult,
  FetchSurveyTemplateDocument,
  FetchSurveyTemplateElementsDocument,
  FetchSurveyTemplateElementsQueryResult,
  FetchSurveyTemplateQueryResult,
  FetchSurveyTemplatesDocument,
  FetchSurveyTemplatesQueryResult,
  UpdateSurveyTemplateDocument,
  UpdateSurveyTemplateMutationResult,
  UpdateSurveyTemplateStatusDocument,
  UpdateSurveyTemplateStatusMutationResult,
} from '~/gql/graphql';
import * as schemas from '../schemas';
import { RootState } from '../store';
import {
  OutputSurveyTemplate,
  SurveyElementInput,
  SurveyTemplateEmailInput,
  SurveyTemplateName,
} from '~/interfaces/survey_template.interface';
import { SurveyAudienceMode } from '~/interfaces/survey.interface';
import * as transformers from '../transformers';

const fetchAllSurveyTemplates = createAsyncThunk<
  { [templateId: number]: OutputSurveyTemplate },
  { surveyTypeId?: number } | undefined,
  { state: RootState }
>('surveyTemplates/fetchAllSurveyTemplates', async (args, { rejectWithValue }) => {
  let filterOptions = {};
  if (args?.surveyTypeId) {
    filterOptions = { surveyTypeId: args.surveyTypeId };
  }

  const res = await graphqlRequest<FetchSurveyTemplatesQueryResult>(FetchSurveyTemplatesDocument, { filterOptions });
  if (hasGraphQLNetworkError(res)) return rejectWithValue(res.error);

  const surveyTemplates = transformers.surveyTemplatesFrom(res.data?.getSurveyTemplates);

  return normalize(surveyTemplates, [schemas.surveyTemplate]);
});

const addSurveyTemplate = createAsyncThunk<
  { [templateId: number]: OutputSurveyTemplate },
  {
    names: SurveyTemplateName[];
    surveyTypeId: number;
    surveyElements: SurveyElementInput[] | null;
    audienceMode?: SurveyAudienceMode;
    lifecycleSurveyTypeId?: number | null;
  },
  { state: RootState }
>(
  'surveyTemplates/addSurveyTemplate',
  async ({ names, surveyTypeId, surveyElements, audienceMode, lifecycleSurveyTypeId }, { rejectWithValue }) => {
    try {
      const res = await graphqlRequest<AddSurveyTemplateMutationResult>(AddSurveyTemplateDocument, {
        names,
        surveyTypeId,
        surveyElements,
        audienceMode,
        lifecycleSurveyTypeId,
      });
      if (hasGraphQLNetworkError(res)) return rejectWithValue(res.error);

      const surveyTemplate = transformers.surveyTemplateFrom(res.data?.addSurveyTemplate);

      return normalize([surveyTemplate], [schemas.surveyTemplate]);
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

const updateSurveyTemplate = createAsyncThunk<
  { [templateId: number]: OutputSurveyTemplate },
  {
    id: number;
    names?: SurveyTemplateName[];
    surveyElements?: SurveyElementInput[] | null;
    email?: SurveyTemplateEmailInput;
    audienceMode?: SurveyAudienceMode;
    lifecycleSurveyTypeId?: number | null;
  },
  { state: RootState }
>(
  'surveyTemplates/updateSurveyTemplate',
  async ({ id, names, surveyElements, email, audienceMode, lifecycleSurveyTypeId }, { rejectWithValue }) => {
    try {
      const res = await graphqlRequest<UpdateSurveyTemplateMutationResult>(UpdateSurveyTemplateDocument, {
        id,
        names,
        surveyElements,
        email,
        audienceMode,
        lifecycleSurveyTypeId,
      });
      if (hasGraphQLNetworkError(res)) return rejectWithValue(res.error);

      const surveyTemplate = transformers.surveyTemplateFrom(res.data?.updateSurveyTemplate);

      return normalize([surveyTemplate], [schemas.surveyTemplate]);
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

const fetchSurveyTemplate = createAsyncThunk<
  { [templateId: number]: OutputSurveyTemplate },
  { id: number },
  { state: RootState }
>('surveyTemplates/fetchSurveyTemplate', async ({ id }, { rejectWithValue }) => {
  const res = await graphqlRequest<FetchSurveyTemplateQueryResult>(FetchSurveyTemplateDocument, { id });
  if (hasGraphQLNetworkError(res)) return rejectWithValue(res.error);

  const surveyTemplate = transformers.surveyTemplateFrom(res.data?.getSurveyTemplate);

  return normalize([surveyTemplate], [schemas.surveyTemplate]);
});

const fetchSurveyTemplateElements = createAsyncThunk<
  { [templateId: number]: OutputSurveyTemplate },
  { id: number },
  { state: RootState }
>('surveyTemplates/fetchSurveyTemplateElements', async ({ id }, { rejectWithValue }) => {
  const res = await graphqlRequest<FetchSurveyTemplateElementsQueryResult>(FetchSurveyTemplateElementsDocument, { id });

  if (hasGraphQLNetworkError(res)) return rejectWithValue(res.error);

  const surveyTemplate = transformers.surveyTemplateFrom(res.data?.getSurveyTemplate);

  return normalize([surveyTemplate], [schemas.surveyTemplate]);
});

const deleteSurveyTemplate = createAsyncThunk<{ template: number } | unknown, { id: number }>(
  'surveyTemplates/deleteSurveyTemplate',
  async ({ id }, { rejectWithValue }) => {
    const res = await graphqlRequest<DeleteSurveyTemplateMutationResult>(DeleteSurveyTemplateDocument, { id });
    if (hasGraphQLNetworkError(res)) return rejectWithValue(res.error);
    if (!res.data?.deleteSurveyTemplate?.success) return rejectWithValue('Failed to delete survey template');

    return { templateId: id };
  },
);

const updateSurveyTemplateStatus = createAsyncThunk<
  { templateId: number; newStatus: string } | unknown,
  { templateId: number; newStatus: string }
>('surveyTemplates/updateSurveyTemplateStatus', async ({ templateId, newStatus }, { rejectWithValue }) => {
  const res = await graphqlRequest<UpdateSurveyTemplateStatusMutationResult>(UpdateSurveyTemplateStatusDocument, {
    templateId,
    newStatus,
  });
  if (hasGraphQLNetworkError(res)) return rejectWithValue(res.error);
  if (!res.data?.updateSurveyTemplateStatus?.success) return rejectWithValue('Failed to update survey template status');

  return { templateId, newStatus, updated: res.data?.updateSurveyTemplateStatus?.updated };
});

const cloneSurveyTemplate = createAsyncThunk<
  { id: number; names: SurveyTemplateName[] } | unknown,
  { id: number; names: SurveyTemplateName[] }
>('surveyTemplates/cloneSurveyTemplate', async ({ id, names }, { rejectWithValue }) => {
  const res = await graphqlRequest<CloneSurveyTemplateMutationResult>(CloneSurveyTemplateDocument, { id, names });

  if (hasGraphQLNetworkError(res)) return rejectWithValue(res.error);
  if (!res.data?.cloneSurveyTemplate) return rejectWithValue('Failed to clone survey template');

  const surveyTemplate = transformers.surveyTemplateFrom(res.data?.cloneSurveyTemplate);

  return { clonedTemplate: surveyTemplate };
});

export const surveyTemplateActions = {
  fetchAllSurveyTemplates,
  addSurveyTemplate,
  updateSurveyTemplate,
  fetchSurveyTemplate,
  fetchSurveyTemplateElements,
  deleteSurveyTemplate,
  updateSurveyTemplateStatus,
  cloneSurveyTemplate,
};
